import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {AuthService} from "../../service/AuthService";
import {AccountService} from "../../service/AccountService";
import {PricingPlanService} from "../../service/PricingPlanService";
import {formatCurrency, user as base_user, subscription as base_subscription, toTitleCase, isAdmin, user} from "../../utils/Utils";
import {toDateString} from "../../utils/TimeUtil";



import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarAlt, faCashRegister, faHandHoldingUsd} from "@fortawesome/free-solid-svg-icons";
import {faShopify} from "@fortawesome/free-brands-svg-icons";


import BillingAdress from "./BillingAddress";
import PaymentMethod from "./PaymentMethod";
import {Dialog} from "primereact/dialog";
import {Loading} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";


// Wrap $-amounts in the one-time-fee notes so the prices stand out.
const highlightNotePrices = (text) =>
    (text || "").split(/(\$\d+(?:[.,]\d+)?)/g).map((part, i) =>
        /^\$\d+(?:[.,]\d+)?$/.test(part) ? <strong key={i} className="acc-sp-note-price">{part}</strong> : part);

// Strip the leading asterisk marker(s) from a note (we render numbered badges instead).
const stripNoteMarker = (text) => (text || "").replace(/^\s*\*+\s*/, "");

// Convert a trailing asterisk marker on a feature label into a numbered badge
// matching the one-time-fee notes (asterisk count = note number).
const featureLabel = (label) => {
    const m = (typeof label === "string") ? label.match(/^(.*?)\s*(\*+)\s*$/) : null;
    if (!m) return label;
    return <span>{m[1]}<sup className="acc-sp-fmark">{m[2].length}</sup></span>;
};

const Subscription = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const [plansLoading, setPlansLoading] = useState(false);

    const [user, setUser] = useState(base_user());

    // Shopify-app merchants: their billing runs through Shopify. We still SHOW
    // the (Shopify-only) plans here, but billing runs through Shopify: the
    // backend creates a Shopify subscription and returns the approval URL, and
    // we redirect the merchant there (instead of the panel's payment flow).
    const shopifyShop = user?.settings?.shopify_app?.shop;
    const isShopifyUser = !!shopifyShop;
    // Shopify plans all share type "paid" (so the type-based header color is
    // blank); give each a distinct colored header (white text) by plan name.
    // Keyed by plan name, ordered by price tier (Basic < Professional < Business
    // < Premium) so the header color rises with the plan.
    const SHOPIFY_PLAN_HEADER_COLORS = {
        Basic: '#16a34a',
        Professional: '#2563eb',
        Business: '#d97706',
        Premium: '#7c3aed',
    };

    const [subscription, setSubscription] = useState(base_subscription());
    const [pricing_plans, setPricing_plans] = useState([]);
    const [added_additional_features, setAdded_additional_features] = useState([]);
    const [additional_features, setAdditional_features] = useState([]);


    const [subscriptionMonthlyPrice, setSubscriptionMonthlyPrice] = useState(0);
    const [total_additional_feature_price, setTotal_additional_feature_price] = useState(0);
    const [billing_address, setBilling_address] = useState(null);
    const [payment_method, setPayment_method] = useState(null);

    const [show_billing_address, setShow_billing_address] = useState(false);
    const [show_payment_method, setShow_payment_method] = useState(false);

    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [currentPlanId, setCurrentPlanId] = useState(null);

    const [calculatedDebit, setCalculatedDebit] = useState(null);

    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);


    const accountService = new AccountService();
    const pricingPlanService = new PricingPlanService();

    useEffect(() => {
        loadPage();
    }, [currentPlanId]);

    // Shopify merchants return here from the billing approval screen with a
    // charge_id. The plan activation otherwise arrives via an async webhook, so
    // the page would show the old plan until a manual refresh. Confirm the
    // charge with the backend (syncs immediately), then reload.
    const confirmStarted = useRef(false);
    useEffect(() => {
        if (confirmStarted.current || !isShopifyUser) return;
        const qs = (window.location.hash.split('?')[1]) || '';
        const chargeId = new URLSearchParams(qs).get('charge_id');
        if (!chargeId) return;
        confirmStarted.current = true;
        accountService.confirmShopifySubscription(chargeId)
            .then(() => loadPage())
            .catch(() => loadPage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShopifyUser]);

    const loadPage = () =>{
        setPlansLoading(true);
        // For Shopify users, fetch the current subscription fresh (falls back to
        // the cached one) so a plan change synced via webhook shows up without
        // re-login. Web users keep the existing cached-subscription behavior.
        const subscriptionPromise = isShopifyUser
            ? accountService.getMySubscription().then(s => s || subscription).catch(() => subscription)
            : Promise.resolve(subscription);
        subscriptionPromise
            .then(sub => {
                if (isShopifyUser && sub) {
                    setSubscription(sub);
                    // Keep the cached subscription and the header plan badge in
                    // sync after a Shopify plan change.
                    AuthService.setSubscription(sub);
                    if (props.onSubscriptionLoaded) props.onSubscriptionLoaded(sub);
                }
                return pricingPlanService.getPricingPlans().then(pricing_plans => {
            if (pricing_plans)
                pricing_plans.sort(function (a, b) {return (parseFloat(a.monthly_price) - parseFloat(b.monthly_price))});
            let currentPlanId = sub ? sub.pricing_plan_id : null;
            let currentPlan = pricing_plans[0];
            if (currentPlanId) {
                currentPlan = pricing_plans.filter(plan => plan.id === currentPlanId)[0];
                if (currentPlan === undefined) {
                    currentPlan = pricing_plans[0];
                    currentPlanId = null;
                }
            }

            let additional_features = currentPlan.additional_features

            if (currentPlanId) {
                (sub.additional_features || []).forEach(item => {
                    let additional_feature = additional_features.filter(innerItem => innerItem.name === item.name)[0];
                    if (additional_feature) {
                        additional_feature.is_selected = true;
                        additional_feature.price = item.price;
                    }
                })
            }

            let subscriptionMonthlyPrice = null;
            let total_additional_feature_price = 0;
            if (currentPlanId) {
                additional_features.forEach(item => {
                    if (item.is_selected) {
                        total_additional_feature_price += item.price;
                    }
                })
                subscriptionMonthlyPrice = sub.plan_price - total_additional_feature_price;
            }
            setPricing_plans(pricing_plans);
            setSubscriptionMonthlyPrice(subscriptionMonthlyPrice);
            setSelectedPlanId(currentPlanId);
            setCurrentPlanId(currentPlanId);
            setAdditional_features(currentPlan.additional_features.filter(item => item.is_active));
            setTotal_additional_feature_price(total_additional_feature_price);
            setPlansLoading(false);
                });
        }).catch(error => {
            setError(error);
            setError(null);
            setPlansLoading(false);
        });


        accountService.getPaymentMethod().then(payment_method => {
            setPayment_method(payment_method);
            setShow_payment_method(true);
        }).catch(error => {
            setError(error);
            setError(null);
        });


        accountService.getBillingAddress().then(billing_address => {
            setBilling_address(billing_address);
            setShow_billing_address(true);
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const selectPlan = (planId) => {

        let selectedPlan = pricing_plans.filter(plan => plan.id === planId)[0]
        if (selectedPlanId !== planId) {
            let total_additional_feature_price = 0
            let added_additional_features = []
            additional_features.forEach(item => {
                if (item.is_selected) {
                    let additional_feature = selectedPlan.additional_features.filter(innerItem => innerItem.name === item.name)[0];
                    total_additional_feature_price += additional_feature.price;
                    added_additional_features.push(additional_feature);
                    additional_feature.is_selected = true;
                    item.is_selected = false;
                }
            })

            setTotal_additional_feature_price(total_additional_feature_price);
            setAdded_additional_features(added_additional_features);
        }
        setSelectedPlanId(planId);
        setAdditional_features(selectedPlan.additional_features.filter(item => item.is_active));

    };

    const cancelPlan = () =>{
        setSelectedPlanId(null);
        accountService.calculateDebitForPlan(null,"").then(result=> {
            setCalculatedDebit(result);
            setShowModal(true);
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const changeBillingAddress = (billing_address) => {
        setBilling_address(billing_address);
    };

    const changePaymentMethod = (payment_method) =>{
        setPayment_method(payment_method);
    };

    const checkBillingInfo = () =>{
        if (!billing_address)
        {
            toast.current.show({severity: 'warn', summary: t('account.no_billing_address'), detail: t('message_detail.required')});
            return false;
        }
        if (!payment_method)
        {
            toast.current.show({severity: 'warn', summary: t('account.payment_method'), detail: t('message_detail.required')});
            return false;
        }
        return true;
    }

    const applyFinalSubscription = (result, _subscription) => {
        user.balance = result.balance ? result.balance : 0;

        AuthService.setUser({...user});
        AuthService.setSubscription( _subscription);

        setSubscription(_subscription);
        setUser({...user});
        setCalculatedDebit(null);
        setSelectedPlanId(_subscription ? _subscription.pricing_plan_id : null);
        setCurrentPlanId(_subscription ? _subscription.pricing_plan_id : null);
        setShowModal(false);
        setLoading(false);
    }

    const applyChanges = () => {

        if (checkBillingInfo()) {
            setLoading(true);

            if (selectedPlanId) //Update or Renew
            {
                //renew
                if (selectedPlanId === currentPlanId &&
                    (payment_method.method_type === "manuel_credit_card" || (isAdmin() && payment_method.method_type === "money_order") ))
                {
                    accountService.renewMyPlan().then(result=>{

                        applyFinalSubscription(result, result.subscription);

                        toast.current.show({severity: 'success', summary: payment_method.method_type === "manuel_credit_card" ? t('actions.pay') : t('actions.renew_subscription'), detail: t('message_detail.successful')});
                    }).catch(error => {
                        setError(error);
                        setError(null);
                        setLoading(false);
                    });
                }
                else//update
                {
                    let added_additional_feature_names = added_additional_features.map(item => item.name)
                    accountService.changeMyPlan(selectedPlanId, added_additional_feature_names).then(result => {

                        applyFinalSubscription(result, result.subscription);

                        toast.current.show({severity: 'success', summary: t('subscription.change_plan'), detail: t('message_detail.successful')});

                    }).catch(error =>{
                        setError(error);
                        setError(null);
                        setLoading(false);
                    });
                }
            } else //cancel
            {
                accountService.cancelMyPlan().then(result => {

                    applyFinalSubscription(result, null);

                    toast.current.show({severity: 'success', summary: t('subscription.cancel_plan'), detail: t('message_detail.successful')});

                }).catch(error =>{
                    setError(error);
                    setError(null);
                    setLoading(false);
                });
            }
        }
    }

    const addOrRemoveAdditionalFeature = (event, plan, additional_feature) => {
        additional_feature.is_selected = event.value;
        let additional_feature_price = additional_feature.price;
        if (!additional_feature.is_selected) {
            additional_feature_price = -additional_feature_price;

            setAdded_additional_features([...added_additional_features.filter(item => item !== additional_feature)]);
            setTotal_additional_feature_price(total_additional_feature_price + additional_feature_price);

        }
        else {
            added_additional_features.push(additional_feature);
            setAdded_additional_features([...added_additional_features]);
            setTotal_additional_feature_price(total_additional_feature_price + additional_feature_price);
        }
    }

    let planList = "";

    const PLAN_ACCENTS = { basic: '#d97706', professional: '#16a34a', premium: '#2563eb' };

    // strikethrough(plan value) -> red(current subscription value) when the user's
    // active subscription on THIS plan differs (preserves the old matrix behavior).
    const planDiff = (plan, planVal, subVal) =>
        (subscription && subscription.pricing_plan_id === plan.id && subVal !== planVal)
            ? <span><s>{planVal}</s> <span style={{ color: '#dc2626' }}>{subVal}</span></span>
            : <span>{planVal}</span>;

    planList = pricing_plans.map(plan => {
        const isSelected = selectedPlanId === plan.id;
        const isCurrent = currentPlanId === plan.id;
        const accent = isShopifyUser
            ? (SHOPIFY_PLAN_HEADER_COLORS[plan.name] || '#5e8e3e')
            : (PLAN_ACCENTS[plan.type] || '#6b7280');
        const title = isShopifyUser ? (plan.name || toTitleCase(plan.type)) : toTitleCase(plan.type);

        const numericRows = [
            { label: featureLabel(t('subscription.total_competitor_count')),
              value: plan.total_competitor_count ? planDiff(plan, plan.total_competitor_count, subscription && subscription.total_competitor_count) : <span>{t('subscription.unlimited')}</span> },
            { label: featureLabel(t('subscription.total_product_count')),
              value: plan.total_product_count ? planDiff(plan, plan.total_product_count, subscription && subscription.total_product_count) : <span>-</span> },
            { label: featureLabel(t('subscription.daily_price_check_count')),
              value: plan.daily_price_check_count ? planDiff(plan, plan.daily_price_check_count, subscription && subscription.daily_price_check_count) : <span>-</span> },
            { label: featureLabel(t('subscription.manuel_price_check_count')),
              value: (plan.manuel_price_check_count || plan.manuel_price_check_count === 0) ? planDiff(plan, plan.manuel_price_check_count, subscription && subscription.manuel_price_check_count) : <span>-</span> },
        ];
        const boolRows = [
            { label: featureLabel(t('subscription.instant_price_check_enabled')), on: plan.instant_price_check_enabled },
            { label: featureLabel(t('subscription.sale_history_enabled')), on: plan.sale_history_enabled },
            { label: featureLabel(t('subscription.dynamic_pricing_enabled')), on: plan.dynamic_pricing_enabled },
        ];

        const addons = isSelected ? additional_features : (plan.additional_features || []).filter(f => f.is_active);

        return (
            <div className={"acc-sp-card" + (isCurrent ? " is-current" : (isSelected ? " is-selected" : ""))} key={plan.id}
                 onClick={() => selectPlan(plan.id)}>
                <div className="acc-sp-top" style={{ borderTopColor: accent }}>
                    <div className="acc-sp-name">
                        <span style={{ color: accent }}>{title}</span>
                        {isCurrent && <span className="acc-sp-badge">{t('actions.current')}</span>}
                    </div>
                    <div className="acc-sp-price">
                        {plan.monthly_price
                            ? ((subscription && subscription.pricing_plan_id === plan.id && subscriptionMonthlyPrice !== plan.monthly_price)
                                ? <span><s>{"$" + plan.monthly_price}</s> <span style={{ color: '#dc2626' }}>{"$" + subscriptionMonthlyPrice}</span></span>
                                : "$" + plan.monthly_price)
                            : "-"}
                        {total_additional_feature_price !== 0 && isSelected
                            ? <span className="acc-sp-addonsum">{" + $" + total_additional_feature_price.toFixed(2)}</span> : ""}
                    </div>
                    <div className="acc-sp-per">{t('subscription.monthly_price')}</div>
                </div>

                <div className="acc-sp-feats">
                    {numericRows.map((r, i) => (
                        <div className="acc-sp-f" key={"n" + i}>
                            <span className="acc-sp-fv">{r.value}</span>
                            <span className="acc-sp-fl">{r.label}</span>
                        </div>
                    ))}
                    {boolRows.map((r, i) => (
                        <div className={"acc-sp-f" + (r.on ? "" : " is-off")} key={"b" + i}>
                            <span className="acc-sp-fv">{r.on ? <i className="pi pi-check acc-sp-check" /> : <span className="acc-sp-dash">-</span>}</span>
                            <span className="acc-sp-fl">{r.label}</span>
                        </div>
                    ))}
                </div>

                {addons.length > 0 &&
                    <div className="acc-sp-addons">
                        <div className="acc-sp-addons-t">{t('subscription.addons', 'Add-ons')}</div>
                        {addons.map((af, i) => (
                            isSelected ?
                                <button type="button" key={"a" + i}
                                        className={"acc-sp-addon" + (af.is_selected ? " is-on" : "")}
                                        onClick={(e) => { e.stopPropagation(); addOrRemoveAdditionalFeature({ value: !af.is_selected }, plan, af); }}>
                                    <span className="acc-sp-addon-box"><i className="pi pi-check" /></span>
                                    <span className="acc-sp-addon-name">{featureLabel(t('subscription.' + af.name))}</span>
                                    <span className="acc-sp-addon-price">{"+$" + af.price}</span>
                                </button>
                                :
                                <div key={"a" + i} className="acc-sp-addon is-static">
                                    <span className="acc-sp-addon-box"></span>
                                    <span className="acc-sp-addon-name">{featureLabel(t('subscription.' + af.name))}</span>
                                    <span className="acc-sp-addon-price">{"+$" + af.price}</span>
                                </div>
                        ))}
                    </div>}

                <div className="acc-sp-foot">
                    <button type="button"
                            className={"acc-sp-btn" + (isCurrent ? " is-current" : (isSelected ? " is-selected" : ""))}
                            onClick={(e) => { e.stopPropagation(); selectPlan(plan.id); }}>
                        {isCurrent ? <span><i className="pi pi-check" /> {t('actions.current')}</span>
                            : (isSelected ? t('actions.selected') : t('actions.select'))}
                    </button>
                </div>
            </div>
        );
    });

    let today = new Date();
    let first28 = today.getDate() < 28 ? today.setDate(28) : new Date(today.setMonth(today.getMonth()+1)).setDate(28);
    let next_start_at = toDateString(first28);

    const modalYesNo = (
        !loading ? <div>
            <Button label={t('actions.confirm')} icon="pi pi-check" className="p-button-outlined p-button-success" onClick={applyChanges} />
            <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-outlined p-button-warning" onClick={() => {setShowModal(false);}}/>
        </div> : <div> </div>
    );


    const changePlan = () => {
        // Shopify merchants: the backend creates a Shopify subscription for the
        // selected plan and returns the approval URL; redirect the merchant
        // there to confirm (14-day trial, then billed via Shopify).
        if (isShopifyUser) {
            setLoading(true);
            // Return to the Subscriptions tab after Shopify approval (?subscription=1
            // is read by the Account page to open this tab).
            const returnUrl = window.location.origin + '/#/account?subscription=1';
            accountService.getShopifySubscriptionUrl(selectedPlanId, returnUrl).then(result => {
                if (result && result.confirmation_url) {
                    window.location.href = result.confirmation_url;
                } else {
                    setLoading(false);
                }
            }).catch(error => {
                setError(error);
                setError(null);
                setLoading(false);
            });
            return;
        }
        let added_additional_feature_names = added_additional_features.map(item => item.name);
        accountService.calculateDebitForPlan(selectedPlanId, added_additional_feature_names.join(",")).then(result=> {
            setCalculatedDebit(result);
            setShowModal(true);
        }).catch(error => {
            setError(error);
            setError(null);
        });
    }

    const moneyOrderMsg = () => {
        let msg = t('subscription.payment.remainder_money_order');
        return msg.toString().split('newline').map(s => {
            return <p>{s}</p>;
        });
    }

    const cancelMsg = () => {
      let msg = t('subscription.confirm_cancel_message');
       return msg.toString().split('newline').map(s => {
           return <p>{s}</p>;
       });
    }

    return <React.Fragment>

        <br/>

        {plansLoading ? <div className="p-col-12" style={{textAlign: 'center'}}>
                <Loading/>
            </div> :

            <div className="p-grid">
                <div className="p-col" style={{minWidth: '47em'}}>
                    <div className="acc-sp-grid">
                        {planList}
                    </div>
                    {!isShopifyUser &&
                    <div className="acc-sp-notes">
                        <div className="acc-sp-notes-h"><i className="pi pi-info-circle" /> {t('subscription.note.header')}</div>
                        <div className="acc-sp-notes-body">
                            {[
                                t('subscription.note.seller_count'),
                                t('subscription.note.api'),
                                t('subscription.note.in_site_search'),
                                t('subscription.note.variant'),
                                t('subscription.note.shipping_price'),
                            ].map((note, i) => (
                                <div className="acc-sp-note" key={i}>
                                    <span className="acc-sp-note-num">{i + 1}</span>
                                    <span>{highlightNotePrices(stripNoteMarker(note))}</span>
                                </div>
                            ))}
                        </div>
                    </div>}

                </div>
                <div className="p-col-fixed p-fluid" style={{width: '25em'}}>
                    <div className="acc-sp-side">

                    {/* Shopify merchants are billed through Shopify, not the
                        panel — hide billing/payment editors and show a notice. */}
                    {isShopifyUser ?
                        <div className="acc-sp-billcard" style={{padding: 14}}>
                            <div style={{fontSize: 13, fontWeight: 600, marginBottom: 4}}>{t('account.payment_method', 'Payment Method')}</div>
                            <p style={{margin: '0 0 12px', fontSize: 12.5, color: '#6b7280'}}>
                                {t('subscription.shopify_payment_note', 'We accept the following payment methods:')}
                            </p>
                            <div style={{display: 'inline-flex', alignItems: 'center', gap: 8,
                                border: '1px solid #e1e3e5', borderRadius: 8, padding: '10px 16px', background: '#fff'}}>
                                <FontAwesomeIcon icon={faShopify} style={{color: '#95BF47', fontSize: 22}}/>
                                <span style={{fontWeight: 600, fontSize: 16, color: '#5e8e3e'}}>Shopify</span>
                            </div>
                        </div>
                        :
                        <React.Fragment>
                            {show_billing_address &&
                                <BillingAdress billing_address={billing_address} onDataChange={changeBillingAddress}/>}

                            {show_payment_method &&
                                <PaymentMethod payment_method={payment_method} onDataChange={changePaymentMethod}/>}
                        </React.Fragment>
                    }

                    {!isShopifyUser &&
                    <div className="acc-sp-billcard">
                        <div className="acc-sp-bill-row">
                            <span className="acc-sp-bill-label"><FontAwesomeIcon icon={faHandHoldingUsd}/> {t('subscription.current_balance')}</span>
                            <span className="acc-sp-bill-val">{formatCurrency(user.balance, "$")}</span>
                        </div>
                    </div>}

                    {!isShopifyUser && ((subscription && subscription.type !== "trial") || selectedPlanId) &&
                        <div className="acc-sp-billcard">
                            <div className="acc-sp-bill-row acc-sp-bill-row--accent"
                                 style={{background: (PLAN_ACCENTS[pricing_plans.find(plan => plan.id === selectedPlanId)?.type] || '#16a34a')}}>
                                <span className="acc-sp-bill-label"><FontAwesomeIcon icon={faCalendarAlt}/> {t('subscription.monthly_billing')}</span>
                                <span className="acc-sp-bill-val">{subscription && subscription.plan_price && selectedPlanId === subscription.pricing_plan_id ? formatCurrency(subscription.plan_price, "$") :
                                    pricing_plans && (pricing_plans.find(plan => plan.id === selectedPlanId) ?
                                        formatCurrency(pricing_plans.find(plan => plan.id === selectedPlanId).monthly_price + total_additional_feature_price, "$") : "$0.00")}</span>
                            </div>
                            <div className="acc-sp-bill-sub">
                                <span>{t('subscription.next_billing_date')}</span>
                                <span>{subscription.finish_at}</span>
                            </div>
                        </div>
                    }

                    <div className="acc-sp-actions">
                        {selectedPlanId !== null && selectedPlanId !== currentPlanId &&
                            <Button label={currentPlanId ? t('actions.changePlan') : t('actions.getPlan')} icon="pi pi-check" className="p-button-success" onClick={changePlan}/>}

                        {selectedPlanId !== null && selectedPlanId === currentPlanId && payment_method && (payment_method.method_type === "manuel_credit_card" || (isAdmin() && payment_method.method_type === "money_order")) &&
                            <Button label={payment_method.method_type === "manuel_credit_card" ? t('actions.pay') : t('actions.renew_subscription')} icon="pi pi-money-bill" className="p-button-success" onClick={() => {
                                setShowModal(true);
                            }}/>}

                        {!isShopifyUser && subscription && subscription.type !== "" && subscription.type !== "trial" &&
                            <Button label={t('actions.cancel_subscription')} icon="pi pi-times" className="p-button-danger" onClick={cancelPlan}/>}
                    </div>

                    </div>
                </div>
                {/*{user.country === "TR" &&*/}
                {/*<div className="p-col-12 p-text-center">*/}
                {/*    <i className="pi pi-home p-mr-1"/>*/}
                {/*    Hacılar Mahallesi 1470/1 Sokak No:15 Gölbaşı / Ankara, TR*/}
                {/*    <i className="pi pi-phone p-ml-4 p-mr-1"/>*/}
                {/*    +90 312 444 4 996*/}
                {/*</div>}*/}
            </div>
        }
        {showModal &&
        <Dialog header={selectedPlanId !== null ? t('subscription.confirm_payment') : t('actions.cancel_subscription')}  footer={modalYesNo} visible={showModal} style={{maxWidth: '50vw', minWidth: '20vw'}} modal={true}
                onHide={() => {setShowModal(false);}}>

            {loading ? <div className="p-col-12" style={{textAlign: 'center'}}>
                    <Loading/>
                </div> :
                selectedPlanId !== null ?
                    (selectedPlanId === currentPlanId && (payment_method.method_type === "manuel_credit_card" || (isAdmin() && payment_method.method_type === "money_order")))
                        ? <div>
                            {moneyOrderMsg()}
                        </div> :
                        <div className="p-grid card">
                            <div className="p-col-12" style={{borderBottom: "1px solid black"}}>
                                <h4><FontAwesomeIcon icon={faCashRegister}/> {t('subscription.due_day')}</h4>
                            </div>
                            <div className="p-col-8">{t('subscription.next_billing_period')}</div>
                            <div className="p-col-4" style={{textAlign: 'right'}}>{toDateString(new Date())} {next_start_at}</div>

                            {(calculatedDebit.current_user_balance !== calculatedDebit.new_user_balance) &&
                            <div className="p-grid p-col-12 p-nogutter">
                                <div className="p-col-8">{t('subscription.current_balance')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.current_user_balance, "$")}</div>
                                <div className="p-col-8">{t('subscription.new_balance')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.new_user_balance, "$")}</div>
                            </div>}
                            {!calculatedDebit.debit_tl && calculatedDebit.debit !== 0 &&
                            <div className="p-grid p-col-12 p-nogutter">
                                <div className="p-col-8">{t('subscription.upgrade_fee')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.debit, "$")}</div>
                            </div>}
                            {(!calculatedDebit.debit_tl && calculatedDebit.debit !== 0 && calculatedDebit.debit_with_tax !== 0 &&
                                calculatedDebit.debit !== calculatedDebit.debit_with_tax) &&
                            <div className="p-grid p-col-12 p-nogutter">
                                <div className="p-col-8">{t('subscription.upgrade_fee_with_tax')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.debit_with_tax, "$")}</div>
                            </div>}

                            {calculatedDebit.debit_tl && calculatedDebit.debit_tl !== 0 &&
                            <div className="p-grid p-col-12 p-nogutter">
                                <div className="p-col-8">{t('subscription.upgrade_fee')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.debit_tl, "TL")}</div>
                            </div>}
                            {(calculatedDebit.debit_tl && calculatedDebit.debit_tl !== 0 && calculatedDebit.debit_with_tax_tl !== 0 &&
                                calculatedDebit.debit_tl !== calculatedDebit.debit_with_tax_tl) &&
                            <div className="p-grid p-col-12 p-nogutter">
                                <div className="p-col-8">{t('subscription.upgrade_fee_with_tax')}</div>
                                <div className="p-col-4" style={{textAlign: 'right'}}>{formatCurrency(calculatedDebit.debit_with_tax_tl, "TL")}</div>
                            </div>}

                        </div> :
                    <div>
                        {cancelMsg()}
                    </div>
            }
        </Dialog>}
    </React.Fragment>;

};
export default Subscription;
